import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CinematicNav from "@/components/CinematicNav";
import Footer from "@/components/Footer";
import { ArrowRight, ExternalLink, Sparkles, Printer, Megaphone, Palette, Tag, UtensilsCrossed, MapPin, Camera } from "lucide-react";

/**
 * Prodotti — pagina dedicata all'ecosistema applicativo TotalKlick.
 * Moduli interni (Hub) + Prodotti autonomi esterni (domini propri).
 * Aria è il sistema nervoso che attraversa tutti.
 */

const INTERNAL_MODULES = [
    {
        id: "totalpl",
        name: "TotalPL",
        tagline: "Stampa intelligente.",
        desc: "Tipografia online con AI: progetti, file pronti per la stampa, preventivi istantanei.",
        accent: "#4F8BFF",
        Icon: Printer,
        to: "/totalpl",
        external: false,
    },
    {
        id: "totalklickads",
        name: "TotalKlickADS",
        tagline: "Social media AI manager.",
        desc: "Pianificazione, creatività e auto-pubblicazione sui social. Calendario drag&drop.",
        accent: "#A855F7",
        Icon: Megaphone,
        to: "/totalklickads",
        external: false,
    },
    {
        id: "design",
        name: "Design & Stampa",
        tagline: "Studio creativo on demand.",
        desc: "Identità visive, packaging, materiali stampati. Direzione artistica integrata.",
        accent: "#9EC3FF",
        Icon: Palette,
        to: "/totalpl",
        external: false,
    },
];

const EXTERNAL_PRODUCTS = [
    {
        id: "offerteincitta",
        name: "OfferteInCittà",
        tagline: "Vetrina locale che cattura.",
        desc: "Portale di offerte territoriali: ogni attività amplia la propria reach con un'audience iperlocale.",
        accent: "#FBBF24",
        Icon: Tag,
        // href rimosso temporaneamente — in attesa nuovo check Google (Feb 2026)
        domain: "offerteincitta.it",
        comingSoon: true,
    },
    {
        id: "ellymenu",
        name: "EllyMenu",
        tagline: "Menu digitali, ordini smart.",
        desc: "QR-menu evoluto per ristoranti, ordini al tavolo, integrazione con cassa e delivery.",
        accent: "#86EFAC",
        Icon: UtensilsCrossed,
        href: "https://elly.offerteincitta.it",
        domain: "elly.offerteincitta.it",
    },
    {
        id: "gostreet",
        name: "GoStreet",
        tagline: "Mobilità urbana, semplificata.",
        desc: "Piattaforma di servizi street e mobility per cittadini e amministrazioni. Locator + booking.",
        accent: "#F97316",
        Icon: MapPin,
        href: "https://gostreet.it",
        domain: "gostreet.it",
    },
    {
        id: "photovendo",
        name: "PhotoVendo",
        tagline: "Vendi le tue foto, ovunque.",
        desc: "Marketplace per fotografi professionisti: store white-label, stampe on demand, license tracking.",
        accent: "#7DD3FC",
        Icon: Camera,
        href: "https://photovendo.com",
        domain: "photovendo.com",
    },
];

export default function Prodotti() {
    useEffect(() => { document.title = "Prodotti — TotalKlick"; }, []);

    return (
        <div className="relative min-h-screen" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="prodotti-page">
            <CinematicNav />

            {/* HERO */}
            <section className="relative pt-32 pb-12 px-5 md:px-10 max-w-6xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ background: "rgba(79,139,255,0.08)", border: "1px solid rgba(79,139,255,0.28)" }}>
                        <Sparkles size={11} className="text-[#9EC3FF]" />
                        <span className="text-[10.5px] uppercase tracking-[0.28em] text-[#9EC3FF]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            le applicazioni dell&apos;ecosistema
                        </span>
                    </div>
                    <h1 className="text-white tracking-tight" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2.4rem, 5vw, 4.4rem)", letterSpacing: "-0.04em", lineHeight: 1.02 }}>
                        Un unico sistema.<br /><span style={{ color: "#9EC3FF" }}>Molte applicazioni.</span>
                    </h1>
                    <p className="text-white/60 mt-5 max-w-2xl text-[15px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Aria attraversa ogni prodotto. I moduli sono indipendenti, ma comunicano. Alcuni vivono dentro
                        il Hub <span className="text-white">totalklick.it</span>, altri hanno il proprio dominio — restando
                        nodi della stessa rete.
                    </p>
                </motion.div>
            </section>

            {/* MODULI HUB */}
            <Section
                eyebrow="hub centrale · totalklick.it"
                title="Moduli del Hub"
                desc="Tre applicazioni native dentro il workspace TotalKlick. Single sign-on, dati condivisi, Aria integrata."
            >
                <div className="grid md:grid-cols-3 gap-3">
                    {INTERNAL_MODULES.map((m, i) => (
                        <ProductCard key={m.id} mod={m} idx={i} />
                    ))}
                </div>
            </Section>

            {/* PRODOTTI ESTERNI */}
            <Section
                eyebrow="prodotti autonomi · domini propri"
                title="Network esterno"
                desc="Quattro prodotti con identità autonoma. Aria li collega all'ecosistema attraverso il Network Matching: visibili, ma fedeli al proprio sito."
            >
                <div className="grid md:grid-cols-2 gap-3">
                    {EXTERNAL_PRODUCTS.map((m, i) => (
                        <ProductCard key={m.id} mod={m} idx={i} external />
                    ))}
                </div>
            </Section>

            {/* ARIA CTA */}
            <section className="relative px-5 md:px-10 max-w-6xl mx-auto py-20 text-center">
                <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                    <div className="text-[11px] uppercase tracking-[0.28em] text-white/40 mb-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        sistema nervoso
                    </div>
                    <h2 className="text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.5vw, 3.6rem)", letterSpacing: "-0.04em", lineHeight: 1.05 }}>
                        Vuoi che Aria viva<br />anche sul <span style={{ color: "#9EC3FF" }}>tuo sito</span>?
                    </h2>
                    <p className="text-white/55 mt-4 max-w-xl mx-auto text-[14.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Genera la tua istanza Aria personalizzata: snippet pronto, host-first policy, lead arricchiti.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center gap-3 justify-center mt-8">
                        <Link
                            to="/aria"
                            className="group inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full text-white text-[14px] transition hover:scale-[1.03]"
                            style={{ background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)", fontFamily: "Geist, sans-serif", fontWeight: 500, boxShadow: "0 20px 50px -12px rgba(79,139,255,0.55)" }}
                            data-testid="prodotti-cta-aria"
                        >
                            Genera la tua Aria
                            <ArrowRight size={14} className="group-hover:translate-x-1 transition" />
                        </Link>
                        <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] text-white/85 hover:text-white transition" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", fontFamily: "Geist, sans-serif" }} data-testid="prodotti-cta-signup">
                            Entra nell&apos;ecosistema
                        </Link>
                    </div>
                </motion.div>
            </section>

            <Footer variant="hub" />
        </div>
    );
}

function Section({ eyebrow, title, desc, children }) {
    return (
        <section className="px-5 md:px-10 max-w-6xl mx-auto py-12">
            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mb-6">
                <div className="text-[10.5px] uppercase tracking-[0.28em] text-white/40 mb-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>{eyebrow}</div>
                <h2 className="text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.8rem, 3.8vw, 2.8rem)", letterSpacing: "-0.03em", lineHeight: 1.08 }}>{title}</h2>
                {desc && <p className="text-white/55 mt-2 max-w-2xl text-[14px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>{desc}</p>}
            </motion.div>
            {children}
        </section>
    );
}

function ProductCard({ mod, idx, external }) {
    const Icon = mod.Icon;
    const inert = !!mod.comingSoon || (external && !mod.href);
    const Wrapper = inert ? "div" : external ? "a" : Link;
    const wrapperProps = inert
        ? {}
        : external
            ? { href: mod.href, target: "_blank", rel: "noreferrer" }
            : { to: mod.to };
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: idx * 0.06, ease: [0.16, 1, 0.3, 1] }}
        >
            <Wrapper
                {...wrapperProps}
                className={`group block rounded-2xl p-5 transition relative overflow-hidden ${inert ? "cursor-default" : "hover:scale-[1.012] hover:bg-white/[0.025]"}`}
                style={{
                    background: "rgba(15,18,36,0.55)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    backdropFilter: "blur(14px)",
                    minHeight: "180px",
                    opacity: inert ? 0.85 : 1,
                }}
                data-testid={`product-${mod.id}`}
            >
                {/* glow accent */}
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full" style={{ background: `radial-gradient(circle, ${mod.accent}33 0%, transparent 70%)`, filter: "blur(20px)", opacity: 0.6 }} />

                <div className="relative">
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="grid place-items-center w-11 h-11 rounded-xl flex-none" style={{
                            background: `radial-gradient(circle at 30% 30%, ${mod.accent}55, ${mod.accent}15 70%)`,
                            border: `1px solid ${mod.accent}44`,
                            boxShadow: `0 0 24px -8px ${mod.accent}66`,
                        }}>
                            <Icon size={18} color={mod.accent} />
                        </div>
                        {inert ? (
                            <span className="inline-flex items-center gap-1 text-[10px] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.14em", textTransform: "uppercase", background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.30)", padding: "3px 8px", borderRadius: 999 }}>
                                in revisione
                            </span>
                        ) : external ? (
                            <span className="inline-flex items-center gap-1 text-[10px] text-white/50 group-hover:text-white/85 transition" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                                {mod.domain} <ExternalLink size={10} />
                            </span>
                        ) : (
                            <span className="text-[10px] text-white/40" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.18em", textTransform: "uppercase" }}>
                                hub
                            </span>
                        )}
                    </div>
                    <div className="text-white text-[18px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{mod.name}</div>
                    <div className="text-[12.5px]" style={{ color: mod.accent, fontFamily: "Outfit, sans-serif", fontWeight: 500 }}>{mod.tagline}</div>
                    <p className="text-white/55 text-[12.5px] mt-2 leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>{mod.desc}</p>
                    {!inert && (
                        <div className="mt-4 inline-flex items-center gap-1.5 text-[11.5px] text-white/60 group-hover:text-white transition" style={{ fontFamily: "Geist, sans-serif" }}>
                            {external ? "Visita" : "Scopri"} <ArrowRight size={11} className="group-hover:translate-x-1 transition" />
                        </div>
                    )}
                </div>
            </Wrapper>
        </motion.div>
    );
}
